// src/store/modules/rating.js
import axios from '@/http/axios/axios';

const state = {
  criteria: [],
  ratingDataSingleQuarter: [], // Данные для одного квартала
  ratingDataYearly: [],         // Годовые данные
  criteriaGroups: {},           // Группировка критериев
  selectedQuarter: '3',
  selectedYear: 2025,
  loading: false,
  error: null,
  savingCells: {},
  inputErrors: {},
};

const getters = {
  getCriteria: (state) => state.criteria,
  getRatingData: (state) => state.ratingDataSingleQuarter,
  getRatingDataYearly: (state) => state.ratingDataYearly,
  getCriteriaGroups: (state) => state.criteriaGroups,
  getSelectedQuarter: (state) => state.selectedQuarter,
  getSelectedYear: (state) => state.selectedYear,
  isLoading: (state) => state.loading,
  getError: (state) => state.error,
  getSavingCells: (state) => state.savingCells,
  getInputErrors: (state) => state.inputErrors,
};

const mutations = {
  SET_LOADING(state, status) {
    state.loading = status;
  },
  SET_ERROR(state, error) {
    state.error = error;
  },
  SET_CRITERIA(state, criteria) {
    state.criteria = criteria;
  },
  SET_RATING_DATA_SINGLE_QUARTER(state, data) {
    // console.log('[MUTATION] SET_RATING_DATA_SINGLE_QUARTER:', data); // Для отладки
    state.ratingDataSingleQuarter = data;
  },
  SET_RATING_DATA_YEARLY(state, data) {
    // console.log('[MUTATION] SET_RATING_DATA_YEARLY:', data); // Для отладки
    state.ratingDataYearly = data;
  },
  SET_CRITERIA_GROUPS(state, groups) {
    // console.log('[MUTATION] SET_CRITERIA_GROUPS:', groups); // Для отладки
    state.criteriaGroups = groups;
  },
  SET_SELECTED_QUARTER(state, quarter) {
    state.selectedQuarter = quarter;
  },
  SET_SELECTED_YEAR(state, year) {
    state.selectedYear = year;
  },
  CLEAR_ERROR(state) {
    state.error = null;
  },
  SET_CELL_SAVING(state, { key, isSaving }) {
    if (isSaving) {
      state.savingCells[key] = true;
    } else {
      delete state.savingCells[key];
    }
  },
  SET_CELL_ERROR(state, { key, error }) {
    if (error) {
      state.inputErrors[key] = error;
    } else {
      delete state.inputErrors[key];
    }
  },
  // Обновление конкретного критерия и пересчёт total_points
  UPDATE_CRITERIA_SCORE(state, { regionId, criterionId, updatedScore }) {
    // console.log('[MUTATION] UPDATE_CRITERIA_SCORE called with:', { regionId, criterionId, updatedScore }); // Для отладки
    const ratingData = [...state.ratingDataSingleQuarter]; // Создаём копию массива
    const regionIndex = ratingData.findIndex(r => r.region.id === regionId);
    if (regionIndex !== -1) {
      const regionEntry = ratingData[regionIndex];
      const criteriaScores = [...regionEntry.criteria_scores]; // Создаём копию массива критериев
      const criterionIndex = criteriaScores.findIndex(cs => cs.criterion.id === criterionId);
      if (criterionIndex !== -1) {
        // Обновляем конкретный критерий
        criteriaScores[criterionIndex] = updatedScore;
        // Пересчитываем total_points как сумму всех points_awarded в criteria_scores
        const total = criteriaScores.reduce((sum, cs) => sum + parseFloat(cs.points_awarded || 0), 0);
        // Обновляем объект региона
        ratingData[regionIndex] = {
          ...regionEntry,
          criteria_scores: criteriaScores,
          total_points: total, // <- Обновляем total_points
        };
        // Пересортировка и переназначение мест
        ratingData.sort((a, b) => b.total_points - a.total_points);
        ratingData.forEach((r, i) => { r.place = i + 1; });
        // Сохраняем в состояние
        state.ratingDataSingleQuarter = ratingData;
        // console.log('[MUTATION] Updated total_points:', total, 'for regionId:', regionId); // Для отладки
      } else {
        // Если критерий не найден, возможно, это первый POST и его нужно добавить
        // console.warn(`[MUTATION] Criterion ID ${criterionId} not found for region ID ${regionId} in criteria_scores.`); // Для отладки
        // Добавляем новый элемент в criteria_scores
        criteriaScores.push(updatedScore); // <- Пример добавления
        // Тогда total и сортировка аналогично обновляются.
        const total = criteriaScores.reduce((sum, cs) => sum + parseFloat(cs.points_awarded || 0), 0);
        ratingData[regionIndex] = {
          ...regionEntry,
          criteria_scores: criteriaScores,
          total_points: total,
        };
        ratingData.sort((a, b) => b.total_points - a.total_points);
        ratingData.forEach((r, i) => { r.place = i + 1; });
        state.ratingDataSingleQuarter = ratingData;
      }
    } else {
      console.warn(`[MUTATION] Region ID ${regionId} not found in ratingDataSingleQuarter.`);
    }
  },
};

const actions = {
  async FETCH_CRITERIA({ commit }, params = {}) {
    commit('SET_LOADING', true);
    commit('CLEAR_ERROR');
    // console.log('[ACTION] FETCH_CRITERIA started'); // Для отладки
    try {
      const response = await axios.get('/rating/criterion/', params);
      const criteriaData = response.data.results || response.data;
      commit('SET_CRITERIA', criteriaData.sort((a, b) => a.id - b.id));
      // console.log('[ACTION] FETCH_CRITERIA success:', criteriaData.length, 'criteria loaded'); // Для отладки
      return Promise.resolve(response);
    } catch (error) {
      commit('SET_ERROR', error.message || 'Ошибка загрузки критериев');
      console.error("[ACTION] FETCH_CRITERIA ERROR:", error);
      return Promise.reject(error);
    } finally {
      // console.log('[ACTION] FETCH_CRITERIA finally'); // Для отладки
    }
  },

  // Исправлено: Используем относительный URL и корректные ключи
  async FETCH_RATING_DATA({ commit, state }, params = {}) {
    commit('SET_LOADING', true);
    commit('CLEAR_ERROR');
    // console.log('[ACTION] FETCH_RATING_DATA started'); // Для отладки

    try {
      const quarter = params.quarter || state.selectedQuarter;
      const year = params.year || state.selectedYear;

      const response = await axios.get('/rating/rating-data/', {
        params: { quarter, year }
      });

      // console.log('[ACTION] FETCH_RATING_DATA response received:', response.data); // Для отладки

      // Исправлено: используем правильные поля из ответа API
      commit('SET_RATING_DATA_SINGLE_QUARTER', response.data.results || []);
      commit('SET_RATING_DATA_YEARLY', response.data.rating_data_yearly || []);
      commit('SET_CRITERIA_GROUPS', response.data.criteria_groups || {});

      // console.log('[ACTION] FETCH_RATING_DATA data committed'); // Для отладки
      return Promise.resolve(response);
    } catch (error) {
      const errorMsg = error.message || 'Ошибка загрузки данных рейтинга';
      commit('SET_ERROR', errorMsg);
      console.error("[ACTION] FETCH_RATING_DATA ERROR:", error);
      return Promise.reject(error);
    } finally {
      // console.log('[ACTION] FETCH_RATING_DATA finally: setting loading to false'); // Для отладки
      commit('SET_LOADING', false);
    }
  },

  UPDATE_SELECTED_QUARTER({ commit }, quarter) {
    commit('SET_SELECTED_QUARTER', quarter);
  },

  UPDATE_SELECTED_YEAR({ commit }, year) {
    commit('SET_SELECTED_YEAR', year);
  },

  // Исправлено: Обработка PATCH/POST и обновление состояния
  async SAVE_MANUAL_SCORE({ commit, dispatch }, payload) {
    const { regionId, criterionId, quarter, points_awarded, evidence, existingId } = payload;
    const cellKey = `${regionId}-${criterionId}`;

    // Проверим тип quarter перед отправкой
    const quarterAsInt = parseInt(quarter, 10);
    if (isNaN(quarterAsInt) || quarterAsInt < 1 || quarterAsInt > 4) {
      console.error('[SAVE_MANUAL_SCORE] Invalid quarter value:', quarter);
      const errorMessage = 'Неверное значение квартала.';
      commit('SET_CELL_ERROR', { key: cellKey, error: errorMessage });
      return Promise.reject(new Error(errorMessage));
    }

    const requestData = {
      region: regionId,
      criterion: criterionId,
      quarter: quarterAsInt, // Отправляем как число
      points_awarded: parseFloat(points_awarded),
      evidence: evidence || '',
    };

    // console.log('[SAVE_MANUAL_SCORE] Request Data:', requestData); // Для отладки

    commit('SET_CELL_SAVING', { key: cellKey, isSaving: true });
    commit('SET_CELL_ERROR', { key: cellKey, error: null });

    try {
      let response;
      if (existingId) {
        // console.log(`[SAVE_MANUAL_SCORE] PATCH to /rating/criterion-execution-manual/${existingId}/`);
        response = await axios.patch(`/rating/criterion-execution-manual/${existingId}/`, requestData);
      } else {
        // console.log(`[SAVE_MANUAL_SCORE] POST to /rating/criterion-execution-manual/`);
        response = await axios.post('/rating/criterion-execution-manual/', requestData);
      }
      // `response.data` содержит обновлённую/созданную запись
      // Она должна соответствовать структуре, ожидаемой в `criteria_scores`
      const updatedScore = {
        criterion: response.data.criterion,
        points_awarded: response.data.points_awarded,
        evidence: response.data.evidence,
        execution_id: response.data.id, // Ключевой момент: id записи
      };
      // console.log('[SAVE_MANUAL_SCORE] Updated score to commit:', updatedScore); // Для отладки
      commit('UPDATE_CRITERIA_SCORE', { regionId, criterionId, updatedScore });
      return Promise.resolve(response);
    } catch (error) {
      const errorResponseData = error.response?.data;
      console.error("[ACTION] SAVE_MANUAL_SCORE ERROR:", error);
      console.error("[ACTION] Request Data:", requestData);
      console.error("[ACTION] Error Response:", errorResponseData);

      const errorMessage = errorResponseData?.detail || errorResponseData?.non_field_errors?.[0] || error.message || 'Ошибка сохранения';
      commit('SET_CELL_ERROR', { key: cellKey, error: errorMessage });
      return Promise.reject(new Error(errorMessage));
    } finally {
      commit('SET_CELL_SAVING', { key: cellKey, isSaving: false });
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};